import { post } from 'epics/ajax';
import { has, get, isNil } from 'lodash';
import type { AjaxResponse } from 'rxjs/observable/dom/AjaxObservable';
import type { FormInput, FormErrors } from 'view/request-create-form';
import { isClientErrorCode } from '@atlassian/help-center-common-util/error-codes';
import { contextPath, getRelativeBasePath } from '@atlassian/help-center-common-util/history';
import type {
    FieldErrors,
    RequestDetailsResponse,
    ErrorMessage,
    RequestCreateAjaxError,
    SubmissionError,
} from './types';
import type { NestedFieldValueType, FieldValueType, AttachmentFile } from 'view/common/fields/types';

export const createRequest = (
    portalId: number,
    requestId: number,
    projectId: string,
    xsrfToken: string,
    formInput: FormInput,
    proformaFormInput: Record<string, unknown> | undefined
): Promise<RequestDetailsResponse | FormErrors> => {
    const url = `${contextPath}${getRelativeBasePath()}/portal/${portalId}/create/${requestId}`;
    const body = serializeFormInput(formInput, projectId, xsrfToken, proformaFormInput);
    const headers = { 'Content-Type': 'application/x-www-form-urlencoded;' };

    return post(url, body, headers)
        .map(({ response }: AjaxResponse) => response as RequestDetailsResponse)
        .toPromise()
        .catch((err) => handleSubmissionErrors(err));
};

/** Turns a potentially undefined or null input and outputs a string */
const handleUndefinedInput = <T,>(input: T | undefined | null) => (!isNil(input) ? input : '');

// Exposed for testing
export const reduceToFormInput: (fieldId: string, fieldValue: FieldValueType) => FormInput[] = (
    fieldId,
    fieldValue
) => {
    let result: FormInput[] = [];

    // If fieldValue is undefined or null it'll be sent as an empty string
    const value = handleUndefinedInput(fieldValue);

    // If the value is an array we want to handle it, each value of the array will be added as a separate entry
    // all with the same field id
    if (Array.isArray(value)) {
        if (fieldId === 'attachment') {
            (value as AttachmentFile[]).forEach((val) => {
                result.push({ name: 'filetoconvert', value: val.id });
            });
        } else {
            (value as string[]).forEach((val) => {
                result.push({ name: fieldId, value: handleUndefinedInput(val) });
            });
        }

        return result;
    }

    if (has(value, 'children')) {
        // For nested fields like cascading select, we add both the value of the primary select as well
        // as the value of any nested children. The field id of child field is the field id of the primary field
        // appended with ':1'
        const { primaryValue, children } = value as NestedFieldValueType;
        result.push({ name: fieldId, value: handleUndefinedInput(primaryValue) });
        result = result.concat(reduceToFormInput(`${fieldId}:1`, children));

        return result;
    }

    // If the field is neither an object nor an array we will serialise the value into a string, undefined and null
    // values will be sent too as empty strings
    result.push({ value, name: fieldId });

    return result;
};

// Some fields require mulitple values for the same key - which can't be handled by a plain JS object.
// here fieldValues is an array with the shape [ { fieldId: fieldValue } ], which gets transformed into a x-www-form-urlencoded string.
function serializeFormInput(
    formInput: FormInput,
    projectId: string,
    xsrfToken: string,
    proformaFormInput: Record<string, unknown> | undefined
): string {
    const fieldValues = Object.keys(formInput).reduce(
        (result, fieldId) => result.concat(reduceToFormInput(fieldId, formInput[fieldId])),
        [] as FormInput[]
    );

    fieldValues.push({ name: 'atl_token', value: xsrfToken });
    fieldValues.push({ name: 'projectId', value: projectId });

    const formUrlEncoded: string = fieldValues
        .map(({ name, value }) => {
            return `${encodeURIComponent(name)}=${encodeURIComponent(value)}`;
        })
        .join('&');

    // Append proforma form fields JSON if there is a proforma form in this request type.
    if (proformaFormInput) {
        return `${formUrlEncoded}&proformaFormData=${encodeURIComponent(JSON.stringify(proformaFormInput))}`;
    }

    return formUrlEncoded;
}

function handleSubmissionErrors({ response, xhr, ...err }: RequestCreateAjaxError): Promise<FormErrors> {
    const DEFAULT_ERROR_MESSAGES: ErrorMessage[] = [];
    const errorMessages = get(response, 'errors', DEFAULT_ERROR_MESSAGES);
    const errorReasonKey = get(response, 'reasonKey', undefined);
    const RATE_LIMITING_STATUS_CODE = 429;

    const fieldErrorsList = errorMessages.filter((error) => !!error.field);
    const submissionErrors = errorMessages.filter((error) => !error.field);
    const hasFieldErrors = fieldErrorsList.length > 0;
    const hasSubmissionError = submissionErrors.length > 0;

    const fieldErrors = hasFieldErrors
        ? fieldErrorsList.reduce((allErrors, error) => {
              allErrors[error.field as string] = error.errorMessage;
              return allErrors;
          }, {} as FieldErrors)
        : {};

    const traceId = xhr.getResponseHeader('Atl-Traceid');

    if (hasSubmissionError) {
        // eslint-disable-next-line prefer-promise-reject-errors
        return Promise.reject({ fieldErrors, errorReasonKey, traceId, submissionError: submissionErrors[0] });
    }

    if (hasFieldErrors) {
        // Suppressing existing violation. Please fix this.
        // eslint-disable-next-line prefer-promise-reject-errors
        return Promise.reject({ fieldErrors, errorReasonKey, traceId, submissionError: undefined });
    }

    if (err.status === 0) {
        // eslint-disable-next-line prefer-promise-reject-errors
        return Promise.reject({ fieldErrors, traceId, submissionError: { isOfflineError: true } });
    }
    /* Any errors between Status code 400 and 499 are considered client errors. */
    const isClientError = isClientErrorCode(err.status);
    const isThrottled = err.status === RATE_LIMITING_STATUS_CODE;
    /* If the traceId is null, that means that the request never reached Atlassian network.
    So, we will not log it as an error since its not from our systems. */
    const hasEmptyTraceId = traceId === null;

    const isGeneric = !(isThrottled || isClientError || hasEmptyTraceId);

    const submissionError: SubmissionError = {
        isGeneric,
        statusCode: err.status,
    };

    // eslint-disable-next-line prefer-promise-reject-errors
    return Promise.reject({
        fieldErrors,
        submissionError,
        traceId,
    });
}
